/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent } from 'react';

import { PreviewType } from 'client-code/previews';

import { TIVO_SERVICES } from '../../../config';
import { ImageAssetsService } from '../../../core/infrastructure';
import { ServicesClientImpl } from '../../../core/interactors';
import { TOfferPreview } from '../../../core/types';
import { PreviewComponent, PreviewComponentAssets } from '../../../core/usecases';
import {
    ActionButton,
    Badge,
    ChannelAiringInfo,
    ContentDescription,
    ContentTitle,
    Credits,
    Divider,
    Duration,
    FirstAirsOrAired,
    Genres,
    ParentalControlRating,
    Separator,
    ShowcaseBanner,
    StatusMessage,
    TimeAiringInfo,
} from '../ui-kit';
import { InfoCardProps } from './InfoCardProps';

/**
 * NonEpisodicInfoCard component
 * Ref.: http://w3-engr/MyWorld/UserInterface/Hydra/Specs/STB/features/smart_tv_app/specific_behavior_changes/content.html#info_cards
 * UX Ref. https://app.zeplin.io/project/6266f85e6716281480fdbbd8/screen/68c3da4cf651b17caccd3c2b
 */
const NonEpisodicInfoCard: FunctionComponent<InfoCardProps> = ({
    metadata,
    properties,
    isActionMode,
    userActions,
    highlight,
}) => {
    if (!metadata) return null;
    const previewInfo = {
        ...metadata,
        ...((metadata as TOfferPreview).offerPreview || {}),
        ...(((metadata as TOfferPreview).offerPreview || {}).contentPreview || {}),
    };
    if (previewInfo.previewType !== PreviewType.ProgramPreview) return null;

    const assetService = new ImageAssetsService({
        imageHostUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
    });
    const url: string = assetService.getEpisodeBannerImageUrlForVodContent(
        previewInfo.contentId || '',
    );
    const pcRatingNames = PreviewComponent.getParentalRatingNames(
        previewInfo?.internalRatingList || [],
    );
    const oPreviewAssets = new PreviewComponentAssets();
    const sContentDescription =
        previewInfo.description ||
        oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.DESCRIPTION_NOT_AVAILABLE,
        );
    // TODO : Need to add VOD_INFO_CARD
    // const vodPreviewFormatter = new VodPreviewFormatter(
    //     contentPreviewInfo,
    //     VodScreenType.VOD_INFO_CARD,,
    // );
    // const entitlementAndAvailabilityStatus = vodPreviewFormatter.getCombinedStatusMessage();
    const entitlementAndAvailabilityStatus = '';

    const drawInfoCard = () => {
        return (
            <div className="info-card-container">
                {/* <div className="info-card metadata-only"> 
                to show metadata only in scroll mode */}
                <div className="info-card">
                    <div className="info-card-header">
                        <div
                            className={
                                properties?.hasItemsBefore
                                    ? 'left-nav-arrow'
                                    : 'left-nav-arrow hidden'
                            }
                        ></div>
                        {/* <div className="showcase-banner-metadata">
                        to switch between banner with metadata view */}
                        <div className="showcase-banner-metadata banner-only">
                            <ShowcaseBanner
                                bannerUrl={url || previewInfo.episodePreviewImageUrl || ''}
                                fallbackText={previewInfo.title || ''}
                            />
                        </div>
                        <div
                            className={
                                properties?.hasItemsAfter
                                    ? 'right-nav-arrow'
                                    : 'right-nav-arrow hidden'
                            }
                        ></div>
                    </div>
                    <div className="info-card-body">
                        <StatusMessage
                            entitlementAndAvailabilityStatus={entitlementAndAvailabilityStatus}
                            status={previewInfo.recordingStatus}
                            isKeepForeverRecording={previewInfo.isKeepForeverRecording}
                            showType={previewInfo.previewType}
                            isPartialRecording={previewInfo.isPartialRecording}
                            startTime={previewInfo.startTime}
                            endTime={previewInfo.endTime}
                            duration={previewInfo.recordingDuration}
                            recordingAvailability={previewInfo.recordingAvailability}
                        />
                        <ContentTitle title={previewInfo.title} />
                        {!entitlementAndAvailabilityStatus && (
                            <>
                                <TimeAiringInfo
                                    startTime={previewInfo.startTime}
                                    endTime={previewInfo.endTime}
                                />
                                <ChannelAiringInfo
                                    channelNumber={previewInfo.channel?.channelNumber}
                                    callSign={previewInfo.channel?.callSign}
                                    videoResolution={
                                        previewInfo.channel?.videoResolution ||
                                        previewInfo.videoResolution
                                    }
                                    isThreeD={previewInfo.isThreeD}
                                />
                            </>
                        )}
                        {entitlementAndAvailabilityStatus && (
                            <Badge
                                text={
                                    previewInfo.videoResolution ||
                                    previewInfo.channel?.videoResolution
                                }
                                uppercase={true}
                            />
                        )}
                        <Badge text={previewInfo.isNew ? 'New' : ''} />
                        <ParentalControlRating ratingNames={pcRatingNames} />
                        {entitlementAndAvailabilityStatus &&
                            previewInfo.videoResolution &&
                            previewInfo.isNew &&
                            pcRatingNames.length > 0 && <Separator />}
                        <Genres categoryLabel={previewInfo.categoryLabel} />
                        {previewInfo.categoryLabel && <Separator />}
                        <Duration duration={previewInfo.duration} />
                        {previewInfo.duration && <Separator />}
                        <FirstAirsOrAired firstAiredDate={previewInfo.firstAiredDate} />
                        <Credits creditString={previewInfo.creditString} lines={1} />
                        <ContentDescription
                            description={sContentDescription}
                            className="primary-text-style text-light mt-16"
                        />
                    </div>
                    <div className="info-card-footer">
                        <Divider />
                        {!isActionMode && <ActionButton type="options" highlighted={true} />}
                        {isActionMode &&
                            (userActions.length > 0 ? (
                                userActions.map((userAction, idx) => (
                                    <ActionButton
                                        key={userAction.id}
                                        highlighted={idx === highlight}
                                    >
                                        {userAction.text}
                                    </ActionButton>
                                ))
                            ) : (
                                <ActionButton type="info" highlighted={true} />
                            ))}
                    </div>
                </div>
            </div>
        );
    };

    return <React.Fragment>{drawInfoCard()}</React.Fragment>;
};

export default NonEpisodicInfoCard;
